import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { TabIndexPipe } from '../../pipes/tabindex.pipe';
import { OverlayService } from '../overlay/overlay.service';

@Component({
    selector: 'oir-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.less']
})
export class ModalComponent implements AfterViewInit {

    TAB_KEYCODE = 9;

    @Input() showCancel = true;
    @Input() class = '';

    @Output() readonly modalClose: EventEmitter<any> = new EventEmitter();

    @ViewChild('modalHeight', { static: true }) modalHeight: ElementRef;
    @ViewChild('modalDialog', { static: true }) modalDialog: ElementRef;

    overflowClass = '';
    focusable: any;
    private visibleState = false;
    private keyDownEventListener: () => void;

    set visible(visible: boolean) {

        const element = this.modalHeight.nativeElement;
        if (visible) {

            setTimeout(() => {
                this.overlayService.show(true);
                this.registerListener();
                const viewHeight = element.offsetHeight;
                // Check if content of modal exceeds browser window.
                if (viewHeight < window.innerHeight) {
                    // Remove attached scroll bar if exists and is not needed.
                    if (this.overflowClass.indexOf('modalOverflow') > -1) {
                        this.overflowClass = '';
                    }
                    this.renderer.setStyle(element, 'marginTop', `-${viewHeight / 2}px`);
                } else {
                    // Move model to top of page by getting its top position less its offset to parent height.
                    const topOfPage = element.getBoundingClientRect().top - element.offsetTop;
                    this.renderer.setStyle(element, 'marginTop', `-${topOfPage}px`);
                    // Set height of modal to match brower window.
                    this.renderer.setStyle(element, 'height', `${window.innerHeight}px`);
                    // Don't add duplicate class.
                    if (this.overflowClass.indexOf('modalOverflow') === -1) {
                        // Add scrollbar.
                        this.overflowClass = 'modalOverflow';
                    }
                }
                this.visibleState = visible;
                this.setTabIndex(this.visibleState);
            }, 500);
        } else {
            this.overlayService.hide(true);
            this.visibleState = visible;
            this.setTabIndex(this.visibleState);
            if (this.keyDownEventListener) {
                this.keyDownEventListener();
            }
        }
    }
    get visible(): boolean { return this.visibleState; }

    constructor(private readonly el: ElementRef,
                private readonly overlayService: OverlayService,
                private readonly renderer: Renderer2) {
    }

    ngAfterViewInit(): void {
        // Get all tabbable elements in document.
        this.focusable = document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        this.setTabIndex(this.visibleState);
    }

    close(): void {
        this.visible = false;
        this.modalClose.emit();
    }

    toggle(): void {
        this.visible = !this.visible;
    }

    setTabIndex(visible): void {
        new TabIndexPipe().transform(visible, this.modalDialog);
    }

    registerListener(): void {
        this.keyDownEventListener = this.renderer.listen('document', 'keydown', (event: any) => {
            // Ignore keydown if its not tab or the current element does not belong to this specific modal.
            if (event.keyCode === this.TAB_KEYCODE && this.el.nativeElement.contains(event.target) && !event.shiftKey) {
                // Work forwards through the tabbable elements.
                for (const focus of this.focusable) {
                    if (this.el.nativeElement.contains(focus)) {
                        // If its the last tabbable element prevent the keydown from executing.
                        if (event.target === focus) {
                            event.preventDefault();
                        }
                        // Ignore disabled elements.
                        if (focus.disabled) {
                            continue;
                        }
                        // Breaks on the first tabbable element belong to this modal.
                        break;
                    }
                }
            } else if (event.keyCode === this.TAB_KEYCODE && this.el.nativeElement.contains(event.target) && event.shiftKey) {
                // Search forwards. Prevent shift tab from working if on first tabbable element in this modal.
                for (const focus of this.focusable) {
                    if (this.el.nativeElement.contains(focus)) {
                        if (event.target === focus) {
                            event.preventDefault();
                        }
                        break;
                    }
                }
            }
        });
    }
}
